import '@lit-labs/virtualizer';
import { ResizeController } from '@lit-labs/observers/resize-controller.js';
import { ApplicationElement, html } from '../application_element/element.js';
import { ChatListController } from './lib/chat_list.controller.js';
import translations from './translations.js';
import style from './style.lit.css';
import '../chat_list_item/element.js';
import '../avatar/element.js';
import '../scroller/element.js';
import '../icon/element.js';
import ChatModel from '../chat/lib/chat.model.js';

import '../chat/lib/global_channel.js';

/**
 * Element shows users list of chats.
 *
 * @prop loadChatsUrl - Where we get chats from? Query params (before, after, search, kind, around, perPage) will be added automatically
 * @prop perPage - How many chats to load per one fetch (optional, default = 10)
 * @prop titleFilter - Show only chats that contain this string in their title (optional, show all chats by default)
 * @prop kindFilter - Show only chats of this kind (options: direct, group, multi), optional
 * @prop muted - Is this a list of muted chats? (optional - default false)
 *
 * @slot loader - Chats loading indicator (default text: Loading...)
 * @slot empty - Empty chat list indicator (default text: Start chatting now)
 * @slot error - For error message that occurred during loading chats (default text: Something went wrong while loading the chats)
 */

export default class ChatList extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  /** @internal */
  static translations = translations;

  static properties = {
    titleFilter: { type: String },
    kindFilter: { type: String },
    perPage: { type: Number },
    muted: { type: Boolean },
    loadChatsUrl: { type: String },
    activateChat: { type: Boolean },
    loading: { type: Boolean },
    firstIndex: { type: Number, state: true },
  };

  /** @internal */
  chatListController = new ChatListController(this);

  /** @internal */
  resizeController = new ResizeController(this, {
    callback: () => true, // observer wouldn't work without callback, but we need it just to trigger a rerender on resize
  });

  constructor() {
    super();
    this.perPage = 50;
    this.muted = false;
    this.activateChat = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this.activateChat && document.addEventListener('message-created', this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.activateChat && document.removeEventListener('message-created', this);
    this.activateChat && ChatModel.setActive(null);
  }

  handleEvent(e) {
    if (e.type === 'message-created') this.onMessageCreateByMe();
  }

  onMessageCreateByMe() {
    if (!this.isOnTop) this.backToTop();
  }

  /** @internal */
  get store() {
    return this.chatListController.value;
  }

  /** @internal */
  get inSearchMode() {
    return Boolean(this.titleFilter);
  }

  /** @internal */
  get initialLoadDone() {
    return this.loading !== undefined;
  }

  /** @internal */
  get isEmpty() {
    return (
      this.initialLoadDone &&
      !(
        this.loading ||
        this.error ||
        this.inSearchMode ||
        this.sortedChats.length
      )
    );
  }

  /** @internal */
  get sortedChats() {
    return this.chatListController.sortedChats;
  }

  /** @internal */
  get activeChatId() {
    return this.chatListController.activeChatId;
  }

  /** @internal */
  get error() {
    return this.chatListController.error;
  }

  /** @internal */
  get emptySearch() {
    return this.inSearchMode && !this.sortedChats.length;
  }

  /** @internal */
  get isFull() {
    return !this.isEmpty;
  }

  /** @internal */
  get emptyList() {
    return this.isEmpty || this.emptySearch;
  }

  /** @internal */
  get notLoading() {
    return !this.loading;
  }

  /** @internal */
  get virtualizer() {
    return this.shadowRoot.querySelector('lit-virtualizer');
  }

  /** @internal */
  get isOnTop() {
    return this.virtualizer?.scrollTop === 0;
  }

  /** @internal */
  get spacerItem() {
    return html`<div class="spacer"></div>`;
  }

  backToTop() {
    this.scrollToIndex(0, 'start');
  }

  scrollToActive() {
    this.scrollToChatId(this.activeChatId);
  }

  findIndexByChatId(chatId) {
    return this.sortedChats.findIndex(c => c.chatId === chatId);
  }

  scrollToChatId(chatId, position = 'center') {
    const index = this.findIndexByChatId(chatId);

    if (index >= 0) this.scrollToIndex(index, position);
  }

  scrollToIndex(index, position = 'nearest') {
    this.virtualizer?.scrollToIndex(index, position);
  }

  handleChatClick(e) {
    if (e.defaultPrevented || !this.activateChat) return;

    const item = e.target.closest('ef-chat-list-item');
    if (!item) return;

    const { uid } = item;
    if (uid === this.activeChatId) return;

    ChatModel.setActive(uid);
  }

  handleVisibilityChange(e) {
    const { first } = e;
    this.firstIndex = first;
    // this.lastIndex = last;
  }

  renderBackToTop() {
    return html`
      <slot name="backToTop" @click=${this.backToTop}>
        <button class="back-to-top">
          <ef-icon name="chevron-up"></ef-icon>
        </button>
      </slot>
    `;
  }

  // keyFunction will prevent re-rendering of the list item when it's not needed
  // Odd but we need to check for chat in keyFunction and renderItem
  renderList() {
    const items = [...this.sortedChats, { spacer: true, chatId: -1 }];

    return html`<lit-virtualizer
      scroller
      @click=${this.handleChatClick}
      @visibilityChanged=${this.handleVisibilityChange}
      .items=${items}
      .keyFunction=${chat => chat?.chatId}
      .renderItem=${chat => {
        if (!chat) return null;

        if (chat.spacer) return this.spacerItem;

        return this.renderItem(chat);
      }}>
    </lit-virtualizer>`;
  }

  // WATCHOUT: This function is probably overridden in projects
  renderItem(item) {
    const {
      chatId,
      title,
      kind,
      isMuted,
      isMarkedAsUnread,
      unreadMessagesCount,
      imageUrl,
      avatarLabel,
      lastSentMessage,
      lastUpdatedAt,
      hasUnsentMessages,
    } = item;

    return html`
      <ef-chat-list-item
        part="ef-chat-list-item"
        uid=${chatId}
        ?active=${this.activateChat && chatId === this.activeChatId}
        name=${title}
        kind=${kind}
        ?mute=${isMuted}
        count=${unreadMessagesCount || Number(!!isMarkedAsUnread)}
        timestamp=${lastUpdatedAt}
        message=${lastSentMessage?.body}
        .messageAttachment=${lastSentMessage?.attachment}
        messageOwnerId=${lastSentMessage?.userId}
        messageOwnerName=${lastSentMessage?.userName}
        ?hasUnsentMessages=${hasUnsentMessages}
        .messageDeleted=${Boolean(lastSentMessage?.deletedAt)}>
        <ef-avatar
          slot="avatar"
          name=${title}
          .picture=${imageUrl}
          rounded
          label=${kind === 'group' ? avatarLabel : null}>
        </ef-avatar>
      </ef-chat-list-item>
    `;
  }

  render() {
    return html`
      ${this.firstIndex > 5 ? this.renderBackToTop() : null}
      ${this.clientHeight && !this.emptyList ? this.renderList() : null}

      <slot name="empty" ?hidden=${this.isFull}>${this.t('empty')}</slot>
      <slot name="empty-search" ?hidden=${!this.emptySearch}>
        ${this.t('empty_search')}
      </slot>
      <slot name="loader" ?hidden=${this.notLoading}>
        ${this.t('loading')}
      </slot>
      <slot name="error" ?hidden=${!this.error}>${this.t('error')}</slot>
    `;
  }
}

ChatList.registerTagName('ef-chat-list');

customElements.get('ef-chat-list') ||
  customElements.define('ef-chat-list', ChatList);
